import i18n from 'i18next';

export class ApiClient {

    constructor(baseUrl, setIsLoading, notify) {
        this.baseUrl = baseUrl;
        this.setIsLoading = setIsLoading;
        this.notify = notify;

        this.loadState();
    }

    loadState() {
        this.state = JSON.parse(localStorage.getItem('state')) || undefined;
    }

    setState(state) {
        this.state = state;

        if (state) {
            localStorage.setItem('state', JSON.stringify(state));
        } else {
            localStorage.removeItem('state');
        }
    }

    getState() {
        return this.state;
    }
    
    async request(endpoint, method = 'GET', data = null, query = null, showLoading = true) {
        if (this.setIsLoading && showLoading) this.setIsLoading(true);

        try {
            const url = new URL(`${this.baseUrl}${endpoint}`);

            if (query) {
                Object.keys(query).forEach(key => url.searchParams.append(key, query[key]));
            }

            const headers = {};
            if (this.state?.token) {
                headers['Authorization'] = `Bearer ${this.state.token}`;
            }

            const options = {
                method,
                headers,
            };

            if (data) {
                if (data instanceof FormData) {
                    options.body = data;
                    // Don't set Content-Type for FormData - browser will set it with boundary
                } else {
                    headers['Content-Type'] = 'application/json';
                    options.body = JSON.stringify(data);
                }
            }

            const response = await fetch(url, options);

            if (response.status === 401) {
                this.logout();
                window.location = '';

                return {
                    hasErrors: true
                };
            }

            const body = await response.json();

            if (this.notify && body.errorType === 'display') {

                if(body.extra)  {
                    Object.keys(body.extra).forEach(key => {
                        body.extra[key] = i18n.t(body.extra[key]);
                    });
                }

                let message = i18n.t(body.message, body.extra);

                this.notify(message, 'error');
            }

            if (response.status !== 200 && response.status !== 201) {
                return {
                    hasErrors: true,
                    message: body.message
                };
            }

            return body;
        } catch (error) {
            if (this.notify) {
                this.notify(error.message, 'error');
            }

            return {
                hasErrors: true
            };
        } finally {
            if (this.setIsLoading && showLoading)
                this.setIsLoading(false);
        }
    }

    logout() {
        this.setState(undefined);
    }

    // QR Endpoints

    getQR(id) {
        return this.request(`/api/qr/${id}`, 'GET');
    }

    // Subscription Endpoints
    createSubscription(data) {
        return this.request('/api/subscription/create', 'POST',
            {
                ...data,
                expires: this.convertToUTC(data.expires),
                starts: this.convertToUTC(data.starts)
            }
        );
    }

    updateSubscription(data) {
        return this.request('/api/subscription/update', 'POST',
            {
                ...data,
                expires: this.convertToUTC(data.expires),
                starts: this.convertToUTC(data.starts)
            });
    }

    spendClass(data) {
        return this.request('/api/subscription/spend-class', 'POST', data);
    }

    cancelSpentClass(data) {
        return this.request('/api/subscription/cancel-spent-class', 'POST', data);
    }

    getSubscriptions(data, showLoading = true) {
        return this.request('/api/subscription/all', 'POST', data, null, showLoading);
    }

    getSubscriptionDetails(id) {
        return this.request(`/api/subscription/${id}`, 'GET');
    }

    deleteSubscription(id) {
        return this.request(`/api/subscription/delete/${id}`, 'DELETE');
    }

    // Tags
    createTag(data) {
        return this.request('/api/tag', 'POST', data);
    }

    updateTag(data) {
        return this.request('/api/tag', 'PUT', data);
    }

    getTags() {
        return this.request('/api/tag', 'GET');
    }

    deleteTag(id) {
        return this.request(`/api/tag/${id}`, 'DELETE');
    }

    // SubscriptionPresets
    createSubscriptionPreset(data) {
        return this.request('/api/subscription-presets', 'POST', data);
    }

    updateSubscriptionPreset(data) {
        return this.request('/api/subscription-presets', 'PUT', data);
    }

    getSubscriptionPresets() {
        return this.request('/api/subscription-presets', 'GET');
    }

    deleteSubscriptionPreset(id) {
        return this.request(`/api/subscription-presets/${id}`, 'DELETE');
    }

    // User Endpoints
    createUser(data) {
        return this.request('/api/user/create', 'POST', data);
    }

    updateUser(data) {
        return this.request('/api/user/update', 'POST', data);
    }

    getUsers(data, showLoading = true) {
        return this.request('/api/user/get', 'POST', data, null, showLoading);
    }

    getManagers(data) {
        return this.request('/api/user/get-managers', 'POST', data);
    }

    login(data) {
        return this.request('/api/user/login', 'POST', data);
    }

    deleteUser(id) {
        return this.request(`/api/user/${id}`, 'DELETE');
    }

    removeCustomer(id) {
        return this.request(`/api/user/remove-customer/${id}`, 'POST');
    }

    resetPassword(data) {
        return this.request('/api/user/reset-password', 'POST', data);
    }

    requestPasswordChange(data) {
        return this.request('/api/user/request-password-change', 'POST', data);
    }

    getCustomer(id) {
        return this.request(`/api/user/get-customer?id=${id}`, 'GET');
    }

    inviteCustomer(data) {
        return this.request(`/api/user/invite-customer`, 'POST', data);
    }

    importCustomers(data) {
        return this.request(`/api/user/import`, 'POST', data);
    }

    acceptInvitation(data) {
        return this.request(`/api/user/respond-invitation`, 'POST', data);
    }

    // general info
    getAdminStats(data) {
        return this.request(`/api/stats/admin`, 'POST', data);
    }

    getManagerStats() {
        return this.request(`/api/stats/manager`, 'GET');
    }

    convertToUTC(date) {
        // Ensure the input date is a valid Date object
        const localDate = new Date(date);
        const now = new Date();

        // Get the UTC equivalent
        const utcDate = new Date(
            localDate.getUTCFullYear(),
            localDate.getUTCMonth(),
            localDate.getUTCDate(),
            now.getHours(),
            now.getMinutes(),
            now.getSeconds()
        );

        return utcDate.toISOString(); // Convert to ISO 8601 string in UTC format
    }
}